import { ExternalLink, FileText, Loader2 } from 'lucide-react';
import { useBadgeAssetQuery } from '@/client/api/badges/badges.query';
import { getAsset } from '@/client/utils/assets';
import { Skeleton } from '@/components/ui/skeleton';

interface EvidencePreviewProps {
  asid: number;
}

const isImageMime = (mime?: string | null) =>
  typeof mime === 'string' && mime.toLowerCase().startsWith('image/');

const EvidencePreview = ({ asid }: EvidencePreviewProps) => {
  const { data: asset, isLoading, isError } = useBadgeAssetQuery(asid);

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
        <Skeleton className="aspect-video w-full" />
        <div className="space-y-1 p-3">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
    );
  }

  if (isError || !asset) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
        <FileText className="h-8 w-8 text-gray-400" />
        <p className="text-xs text-gray-500">Could not load asset {asid}</p>
        <a
          href={`/api/v2/asset/${asid}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
        >
          Open directly <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    );
  }

  const previewUrl = getAsset(asset);
  const mime = (asset as any).mime_type as string | undefined;
  const name =
    (asset as any).name ||
    (asset as any).filename ||
    (asset as any).original_filename ||
    `Asset ${asid}`;
  const showImage = previewUrl && (isImageMime(mime) || !mime);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      {showImage ? (
        <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={previewUrl}
            alt={String(name)}
            className="aspect-video w-full object-contain bg-gray-50"
          />
        </a>
      ) : (
        <div className="flex aspect-video flex-col items-center justify-center gap-2 bg-gray-50 p-4">
          {isLoading ? (
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          ) : (
            <FileText className="h-10 w-10 text-gray-400" />
          )}
          <p className="text-xs text-gray-500">{mime || 'Document'}</p>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-gray-900">{String(name)}</p>
          <p className="text-[11px] text-gray-500">asid {asid}</p>
        </div>
        <a
          href={previewUrl || `/api/v2/asset/${asid}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
        >
          Open <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
};

export default EvidencePreview;
